import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { eventsAPI, citiesAPI, newsAPI } from '../services/api';
import SEOHead from '../components/SEOHead';
import {
  Newspaper,
  ArrowRight,
  TrendingUp,
  ChevronRight,
  MessageCircle,
  Heart,
  Sparkles,
} from 'lucide-react';

const asArray = (data) => (Array.isArray(data) ? data : []);

const TRENDING = [
  { tag: '#DallasMeetup', count: '12.5k' },
  { tag: '#VancouverWatchParty', count: '8.2k' },
  { tag: '#MatchDayCarpool', count: '5.1k' },
];

const FanMeetups = () => {
  const queryClient = useQueryClient();
  const [cityId, setCityId] = useState('');
  const { data: events = [] } = useQuery({
    queryKey: ['events', cityId],
    queryFn: () => eventsAPI.getAll(cityId ? { city_id: cityId } : {}).then((r) => r.data),
  });
  const { data: cities = [] } = useQuery({ queryKey: ['cities'], queryFn: () => citiesAPI.getAll().then((r) => r.data) });
  const { data: news = [] } = useQuery({ queryKey: ['news'], queryFn: () => newsAPI.getAll().then((r) => asArray(r.data)) });

  const mutation = useMutation({
    mutationFn: (data) => eventsAPI.create(data),
    onSuccess: () => queryClient.invalidateQueries(['events']),
  });

  const [form, setForm] = useState({
    title: '',
    team: '',
    description: '',
    location: '',
    date: '',
    link: '',
    city_id: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate({
      ...form,
      city_id: form.city_id ? parseInt(form.city_id, 10) : null,
    });
    setForm({ title: '', team: '', description: '', location: '', date: '', link: '', city_id: '' });
  };

  const topNews = news[0];
  const subNews = news.slice(1, 3);
  const borderRotate = ['border-t-4 border-primary', 'border-t-4 border-secondary', 'border-t-4 border-tertiary'];

  return (
    <>
      <SEOHead
        title="Fan Meetups - World Cup 2026 - WorldCup Fan Planner"
        description="Find and share fan meetups in 2026 FIFA World Cup host cities. Connect with supporters before match day."
      />
      <main className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 font-body">
        <header className="mb-10 sm:mb-12">
          <div className="relative overflow-hidden rounded-4xl kinetic-gradient p-8 sm:p-12 flex flex-col lg:flex-row justify-between items-center gap-8 shadow-xl">
            <div className="relative z-10 max-w-2xl text-center lg:text-left">
              <span className="inline-block px-4 py-1.5 bg-tertiary text-on-tertiary rounded-full text-xs font-bold uppercase tracking-widest mb-5">
                LIVE FROM THE PITCH
              </span>
              <h1 className="text-4xl sm:text-6xl md:text-7xl font-headline font-bold text-white mb-5 leading-tight">The Fan Cockpit.</h1>
              <p className="text-lg sm:text-xl text-on-primary font-medium max-w-lg">
                News, meetups, and community energy across USA, Canada, and Mexico—plan watch parties and connect before kickoff.
              </p>
            </div>
            <div className="relative z-10 flex gap-4 overflow-x-auto pb-1 w-full lg:w-auto justify-center lg:justify-end">
              <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl min-w-[180px] border border-white/10">
                <span className="text-3xl font-black text-white block mb-1">{events.length}</span>
                <span className="text-on-primary text-sm font-bold uppercase tracking-tighter">Meetups listed</span>
              </div>
              <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl min-w-[180px] border border-white/10">
                <span className="text-3xl font-black text-white block mb-1">16</span>
                <span className="text-on-primary text-sm font-bold uppercase tracking-tighter">Host Cities</span>
              </div>
            </div>
            <div className="absolute -right-16 -bottom-20 opacity-15 pointer-events-none hidden sm:block" aria-hidden>
              <Sparkles className="w-64 h-64 text-white" strokeWidth={1} />
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-7 flex flex-col gap-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-3">
              <h2 className="text-2xl sm:text-3xl font-headline font-bold text-primary dark:text-primary-container flex items-center gap-3">
                <Newspaper className="h-7 w-7 text-tertiary shrink-0" aria-hidden />
                Latest News
              </h2>
              <Link to="/news" className="text-primary font-bold text-sm hover:underline inline-flex items-center gap-1">
                View All Stories <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>

            {topNews && (
              <article className="group relative overflow-hidden rounded-3xl bg-surface-container-lowest dark:bg-slate-800/70 shadow-sm hover:shadow-md transition-shadow border border-outline-variant/10 dark:border-slate-700">
                <div className="absolute top-0 left-0 w-1 h-full bg-secondary z-10" aria-hidden />
                <a href={topNews.url} target="_blank" rel="noopener noreferrer" className="block focus:outline-none focus:ring-2 focus:ring-primary rounded-3xl">
                  <div className="h-56 sm:h-64 overflow-hidden relative bg-surface-container-high dark:bg-slate-700">
                    {topNews.image_url ? (
                      <img
                        src={topNews.image_url}
                        alt=""
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary/40 to-secondary/30" aria-hidden />
                    )}
                    <div className="absolute top-4 left-4 flex gap-2">
                      <span className="bg-secondary text-on-secondary px-3 py-1 rounded-lg text-xs font-bold">MATCH DAY</span>
                      <span className="bg-white/85 dark:bg-slate-900/80 backdrop-blur text-primary px-3 py-1 rounded-lg text-xs font-bold">
                        TOP STORY
                      </span>
                    </div>
                  </div>
                  <div className="p-6 sm:p-8">
                    <h3 className="text-xl sm:text-2xl font-headline font-bold text-on-surface dark:text-white mb-3 group-hover:text-primary transition-colors">
                      {topNews.title}
                    </h3>
                    <p className="text-on-surface-variant dark:text-slate-400 leading-relaxed mb-5 line-clamp-3">{topNews.description}</p>
                    <div className="flex flex-wrap justify-between items-center gap-2">
                      <span className="text-sm font-medium text-outline dark:text-slate-500">{topNews.source}</span>
                      <span className="text-primary font-black flex items-center gap-2 text-sm">
                        READ MORE <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" aria-hidden />
                      </span>
                    </div>
                  </div>
                </a>
              </article>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {subNews.map((n, i) => (
                <article
                  key={n.id || n.url || i}
                  className={`p-5 bg-surface-container-lowest dark:bg-slate-800/70 rounded-2xl flex flex-col gap-3 ${i === 0 ? 'border-t-4 border-tertiary' : 'border-t-4 border-secondary'}`}
                >
                  <span className={`text-xs font-bold uppercase tracking-widest ${i === 0 ? 'text-tertiary' : 'text-secondary'}`}>
                    {n.source || 'Update'}
                  </span>
                  <a href={n.url} target="_blank" rel="noopener noreferrer" className="font-headline font-bold text-on-surface dark:text-white hover:text-primary">
                    {n.title}
                  </a>
                  <p className="text-sm text-on-surface-variant dark:text-slate-400 line-clamp-2">{n.description}</p>
                </article>
              ))}
            </div>

            <section aria-labelledby="community-chat-heading">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
                <div>
                  <h2 id="community-chat-heading" className="text-2xl font-headline font-bold text-on-surface dark:text-white">
                    Community pulse
                  </h2>
                  <p className="text-sm text-on-surface-variant dark:text-slate-400 mt-1">Meetups shared by fans—filter by city and post your own.</p>
                </div>
                <Link
                  to="/fan-meetups#create"
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-primary text-on-primary font-bold text-sm hover:brightness-110 transition-all shrink-0"
                >
                  Start a Discussion
                </Link>
              </div>

              <div className="mb-4">
                <label htmlFor="meetup-city-filter" className="sr-only">
                  Filter by city
                </label>
                <select
                  id="meetup-city-filter"
                  value={cityId}
                  onChange={(e) => setCityId(e.target.value)}
                  className="px-4 py-2.5 rounded-xl bg-surface-container-low dark:bg-slate-800 border-none text-on-surface dark:text-slate-100 focus:ring-2 focus:ring-primary"
                >
                  <option value="">All cities</option>
                  {cities.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {events.map((ev, idx) => (
                  <article
                    key={ev.id}
                    className={`rounded-2xl bg-surface-container-lowest dark:bg-slate-800/70 p-5 border border-outline-variant/10 dark:border-slate-700 ${borderRotate[idx % 3]}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <span className="text-xs font-bold text-secondary">#TravelTips</span>
                      <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded font-bold">MEETUP</span>
                    </div>
                    <h3 className="font-headline font-bold text-on-surface dark:text-white mb-2">{ev.title}</h3>
                    <p className="text-sm text-on-surface-variant dark:text-slate-400 line-clamp-2 mb-3">
                      {ev.team && `${ev.team} · `}
                      {ev.city_name || 'Host city'}
                      {ev.location ? ` · ${ev.location}` : ''}
                    </p>
                    {ev.description ? <p className="text-sm text-on-surface-variant mb-3 line-clamp-3">{ev.description}</p> : null}
                    <div className="flex items-center justify-between text-outline dark:text-slate-500 text-xs">
                      <span className="flex items-center gap-3">
                        <span className="inline-flex items-center gap-1">
                          <MessageCircle className="h-3.5 w-3.5" aria-hidden />0
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Heart className="h-3.5 w-3.5" aria-hidden />0
                        </span>
                      </span>
                      {ev.date && <time dateTime={ev.date}>{new Date(ev.date).toLocaleDateString()}</time>}
                    </div>
                    {ev.link && (
                      <a
                        href={ev.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-block text-sm font-bold text-primary hover:underline"
                      >
                        Join / details
                      </a>
                    )}
                  </article>
                ))}
              </div>
              {events.length === 0 && (
                <p className="text-on-surface-variant dark:text-slate-400 py-6 text-center rounded-2xl bg-surface-container-low dark:bg-slate-800/40">
                  No meetups yet for this filter. Be the first to post one.
                </p>
              )}
            </section>
          </div>

          <aside className="lg:col-span-5 flex flex-col gap-8">
            <section className="bg-surface-container-low dark:bg-slate-800/50 rounded-3xl p-6 sm:p-8 border border-outline-variant/10 dark:border-slate-700">
              <h2 className="text-xl font-headline font-bold text-primary dark:text-primary-container mb-6 flex items-center gap-3">
                <TrendingUp className="h-6 w-6" aria-hidden />
                Social Hub
              </h2>
              <div className="space-y-3">
                {TRENDING.map((t, i) => (
                  <Link
                    key={t.tag}
                    to="/fan-meetups"
                    className="flex items-center gap-4 group p-2 rounded-xl hover:bg-surface-container-lowest dark:hover:bg-slate-900/50 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-surface-container-lowest dark:bg-slate-900 flex items-center justify-center font-black text-secondary group-hover:bg-secondary group-hover:text-on-secondary transition-colors">
                      #{i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-on-surface dark:text-white truncate">{t.tag}</p>
                      <span className="text-xs text-outline dark:text-slate-500">{t.count} fans discussing</span>
                    </div>
                    <ChevronRight className="h-5 w-5 text-outline shrink-0" aria-hidden />
                  </Link>
                ))}
              </div>
            </section>

            <section id="create" className="bg-surface-container-lowest dark:bg-slate-800/60 rounded-3xl p-6 sm:p-8 border border-outline-variant/10 dark:border-slate-700 scroll-mt-24">
              <h2 className="text-xl font-headline font-bold text-primary dark:text-primary-container mb-4">Create Meetup</h2>
              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="text"
                  placeholder="Title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-surface-container-low dark:bg-slate-900 border-none text-on-surface dark:text-slate-100 focus:ring-2 focus:ring-primary"
                />
                <input
                  type="text"
                  placeholder="Team (optional)"
                  value={form.team}
                  onChange={(e) => setForm({ ...form, team: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-surface-container-low dark:bg-slate-900 border-none text-on-surface dark:text-slate-100 focus:ring-2 focus:ring-primary"
                />
                <select
                  value={form.city_id}
                  onChange={(e) => setForm({ ...form, city_id: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-surface-container-low dark:bg-slate-900 border-none text-on-surface dark:text-slate-100 focus:ring-2 focus:ring-primary"
                >
                  <option value="">City (optional)</option>
                  {cities.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <textarea
                  placeholder="Description (optional)"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl bg-surface-container-low dark:bg-slate-900 border-none text-on-surface dark:text-slate-100 focus:ring-2 focus:ring-primary resize-y"
                />
                <input
                  type="text"
                  placeholder="Location"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-surface-container-low dark:bg-slate-900 border-none text-on-surface dark:text-slate-100 focus:ring-2 focus:ring-primary"
                />
                <input
                  type="datetime-local"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-surface-container-low dark:bg-slate-900 border-none text-on-surface dark:text-slate-100 focus:ring-2 focus:ring-primary"
                />
                <input
                  type="url"
                  placeholder="Link (optional)"
                  value={form.link}
                  onChange={(e) => setForm({ ...form, link: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-surface-container-low dark:bg-slate-900 border-none text-on-surface dark:text-slate-100 focus:ring-2 focus:ring-primary"
                />
                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="w-full py-3 rounded-xl kinetic-gradient text-on-primary font-bold hover:brightness-110 transition-all disabled:opacity-50"
                >
                  {mutation.isPending ? 'Posting…' : 'Post meetup'}
                </button>
              </form>
            </section>

            <section>
              <h2 className="text-xl font-headline font-bold text-primary dark:text-primary-container mb-4">Fan Meetups</h2>
              <div className="space-y-4">
                {events.slice(0, 4).map((ev, i) => (
                  <div
                    key={ev.id}
                    className="bg-surface-container-lowest dark:bg-slate-800/70 p-5 rounded-2xl flex gap-4 border border-outline-variant/10 dark:border-slate-700"
                  >
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary/30 to-secondary/40 shrink-0" aria-hidden />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start gap-2">
                        <h3 className="font-bold text-on-surface dark:text-white text-sm leading-tight">{ev.title}</h3>
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded font-bold shrink-0 ${
                            i % 3 === 0
                              ? 'bg-primary/10 text-primary'
                              : i % 3 === 1
                                ? 'bg-tertiary/10 text-tertiary'
                                : 'bg-secondary/15 text-secondary'
                          }`}
                        >
                          {i % 3 === 0 ? 'OPEN' : i % 3 === 1 ? 'URGENT' : 'POPULAR'}
                        </span>
                      </div>
                      <p className="text-sm text-on-surface-variant dark:text-slate-400 mt-2 line-clamp-2">
                        {ev.city_name || 'Host city'}
                        {ev.location ? ` · ${ev.location}` : ''}
                      </p>
                      {ev.link && (
                        <a href={ev.link} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-secondary mt-2 inline-flex items-center gap-1">
                          JOIN GROUP <ArrowRight className="h-3 w-3" aria-hidden />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </aside>
        </div>
      </main>
    </>
  );
};

export default FanMeetups;
